import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createCtcLevelTableColumns } from '../../components/ctc-level-table-columns/ctc-level-table-columns';
import { CtcLevelFormDialog } from '../../components/ctc-level-form-dialog/ctc-level-form-dialog';
import { useDeleteCtcLevel, useGetCtcLevels } from '../../hooks/use-ctc-levels';
import { DEFAULT_CTC_LEVELS_ORDER } from '../../graphql/queries';
import { CtcLevel, CtcLevelsPagingResult } from '../../types/ctc-level.types';

export const CtcLevelManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.CTC_LEVELS_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.CTC_LEVELS_UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SETTINGS.CTC_LEVELS_DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_CTC_LEVELS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; ctcLevel: CtcLevel | null }>({
    open: false,
    ctcLevel: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; ctcLevel: CtcLevel | null }>({
    open: false,
    ctcLevel: null,
  });

  const { data, isLoading, error } = useGetCtcLevels({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { CtcLevels?: CtcLevelsPagingResult })?.CtcLevels;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteCtcLevel, isPending: isDeleting } = useDeleteCtcLevel();

  const handleDeleteConfirm = async () => {
    if (!deleteState.ctcLevel) return;
    await deleteCtcLevel(deleteState.ctcLevel.ItemId);
    setDeleteState({ open: false, ctcLevel: null });
  };

  const columns = createCtcLevelTableColumns({
    t,
    onEdit: (ctcLevel) => setFormState({ open: true, ctcLevel }),
    onDelete: (ctcLevel) => setDeleteState({ open: true, ctcLevel }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('CTC_LEVEL_MANAGER')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_CTC_LEVEL'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, ctcLevel: null }),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <CtcLevelFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, ctcLevel: open ? prev.ctcLevel : null }))
        }
        ctcLevel={formState.ctcLevel}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, ctcLevel: open ? deleteState.ctcLevel : null })
        }
        title={t('DELETE_CTC_LEVEL')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
